import { useEffect, useState } from 'react'

export const changeSearchParamByKey = ({
  search,
  queryString,
  defaultValue,
  navigate,
}: {
  queryString: string
  search: string
  defaultValue?: string
  navigate: (path: string) => void
}) => {
  const searchParams = new URLSearchParams(search)

  return [
    searchParams.get(queryString) || defaultValue || null,
    (value: string | number) => setSearchParam({ value, queryString, navigate }),
  ] as const
}

export const changeSearchParamsPairsByKey = ({
  paramPairs,
  search,
  navigate,
}: {
  paramPairs: {
    queryString: string
    defaultValue?: string
  }[]
  search: string
  navigate: (path: string) => void
}) => {
  const searchParams = new URLSearchParams(search)

  const setSearchParams = (
    paramPairs: {
      queryString: string
      value: string
    }[]
  ) =>
    paramPairs.forEach(({ queryString, value }) => setSearchParam({ value, queryString, navigate }))

  const selectedSearchParams = paramPairs.reduce(
    (acc, { queryString, defaultValue }) => {
      acc[queryString] = {
        queryString,
        value: searchParams.get(queryString) || defaultValue || null,
      }

      return acc
    },
    // Not to pollute the object with the upcoming query strings keys
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<
      string,
      {
        queryString: string
        value: string | null
      }
    >
  )

  const [initialSelectedSearchParams] = useState(selectedSearchParams)
  useEffect(() => {
    Object.values(initialSelectedSearchParams).forEach(({ queryString, value }) => {
      if (!value || value === 'off') return

      setSearchParam({ value: value, queryString, navigate })
    })
  }, [initialSelectedSearchParams, navigate])

  return [selectedSearchParams, setSearchParams] as const
}

function setSearchParam({
  value,
  queryString,
  navigate,
}: {
  value: number | string
  queryString: string
  navigate: (path: string) => void
}) {
  const searchParams = new URLSearchParams(location.search)

  if (value || value === 0) {
    searchParams.set(queryString, String(value))
  } else if (queryString) {
    searchParams.delete(queryString)
  }

  if (location.search.replace('?', '') !== searchParams.toString()) {
    navigate(location.pathname + '?' + searchParams.toString())
  }
}

export const changeSortParams = ({
  search,
  navigate,
}: {
  search: string
  navigate: (path: string) => void
}) => {
  const [searchParamPairs, setSearchParamPairs] = changeSearchParamsPairsByKey({
    paramPairs: [{ queryString: 'sort', defaultValue: 'off' }, { queryString: 'sortBy' }],
    search,
    navigate,
  })

  return [
    {
      querySort: searchParamPairs.sort.value,
      querySortBy: searchParamPairs.sortBy.value,
    },
    setSearchParamPairs,
  ] as const
}
